/**
 * Reporte **"Statistics - Room Type"** (`statroomtype`): un PDF por TIPO de
 * habitación, con una fila por día y las columnas `Potential Rms.`, `Rms. Occ.`,
 * `Prs.` y `Room Revenue`.
 *
 * Es el único reporte de la cadena con grano **día × sede × tipo**, y por eso el
 * único que responde «qué tipo se vende y a qué tarifa»: ni el flash ni el
 * Reservation Statistics bajan al tipo de habitación.
 *
 * Tres cosas del formato que ya mordieron sobre los archivos reales de julio
 * 2026 (Caracas):
 *
 * - **Un archivo NO trae todos los días del rango.** Los días sin movimiento
 *   simplemente no se imprimen (a SGP le faltan el 28, 30 y 31). Ausencia no es
 *   cero: un día que falta no genera fila, y el `% ocupación` del tipo se calcula
 *   solo sobre los días que sí están.
 * - **Hay celdas EN BLANCO.** El 29-07 de SGP y SWP trae `Rms. Occ.` vacío. El
 *   Grand Total del propio reporte las suma como 0, así que 0 es la lectura
 *   fiel — pero se avisa, porque una celda vacía en un PDF suele ser el síntoma
 *   de una lectura torcida.
 * - **El `Potential Rms.` del Grand Total NO es la capacidad del mes**: es la
 *   suma de los días impresos. Con días que faltan, sale corto por definición.
 *
 * El cuadre `Σ días = Grand Total` es el control que separa «faltan filas» de
 * «mes flojo», igual que en el reporte por Source.
 */
import { z } from 'zod';
import { isValidIso } from './dates.js';
import { ratio } from './money.js';
import { SEDE_CODES, type SedeCodigo } from './types.js';

const entero = z.number().int().nullable();

export const roomTypeDiaSchema = z.object({
  /** Día de operación, 'YYYY-MM-DD'. */
  fecha: z.string(),
  /** Habitaciones de ese tipo disponibles ese día. */
  potential: entero,
  /** Habitaciones de ese tipo ocupadas ese día: la noche vendida. */
  ocupadas: entero,
  /** Personas alojadas en ellas. */
  personas: entero,
  /** Room Revenue del día, en Bs. */
  room_revenue_bs: z.number().nullable(),
});

export const roomTypeStatSchema = z.object({
  sede_codigo: z.enum(SEDE_CODES).nullable(),
  /** Código del tipo tal cual (SGD/SGP/SGE, TWD/TWP/TWE, SWD/SWP/SWE…). */
  room_type: z.string().nullable(),
  /** Descripción larga («SINGLE ROOM DELUXE»). */
  descripcion: z.string().nullable().default(null),
  dias: z.array(roomTypeDiaSchema).default([]),
  /** Fila «Grand Total» del reporte: el cuadre. */
  grand_total: roomTypeDiaSchema.omit({ fecha: true }).nullable().default(null),
});

export type RoomTypeStat = z.infer<typeof roomTypeStatSchema>;

export interface RoomTypeDia {
  fecha: string;
  potential: number;
  ocupadas: number;
  personas: number;
  roomRevenueBs: number;
}

export interface RoomTypeStatResult {
  sedeCodigo: SedeCodigo | null;
  roomType: string | null;
  descripcion: string | null;
  dias: RoomTypeDia[];
  /** Σ de los días leídos. */
  suma: Omit<RoomTypeDia, 'fecha'>;
  /** Grand Total impreso, o null si el reporte no lo trae. */
  grandTotal: Omit<RoomTypeDia, 'fecha'> | null;
  /** Días con alguna celda vacía en el PDF (se leyeron como 0). */
  diasConBlanco: string[];
  issues: string[];
}

function n(v: number | null | undefined): number {
  return v == null || !Number.isFinite(v) ? 0 : v;
}

/** Normaliza un código de tipo: mayúsculas y sin espacios ni signos. */
export function normalizeRoomType(code: string): string {
  return code.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/**
 * Convierte la extracción en días de ese tipo de habitación, con su cuadre.
 *
 * No inventa los días que faltan: si el reporte no imprime el 30, aquí no hay
 * fila del 30. Rellenarlos con ceros diría que ese día no se vendió nada de ese
 * tipo, que es una afirmación que el reporte no hace.
 */
export function roomTypeFromStat(g: RoomTypeStat): RoomTypeStatResult {
  const issues: string[] = [];
  const dias: RoomTypeDia[] = [];
  const diasConBlanco: string[] = [];
  const vistos = new Set<string>();

  for (const d of g.dias) {
    if (!isValidIso(d.fecha)) {
      issues.push(`Fecha inválida en el reporte: ${d.fecha}.`);
      continue;
    }
    if (vistos.has(d.fecha)) {
      issues.push(`Día repetido en el reporte: ${d.fecha}; se cuenta una vez.`);
      continue;
    }
    vistos.add(d.fecha);
    if (d.potential == null || d.ocupadas == null || d.personas == null) {
      diasConBlanco.push(d.fecha);
    }
    dias.push({
      fecha: d.fecha,
      potential: n(d.potential),
      ocupadas: n(d.ocupadas),
      personas: n(d.personas),
      roomRevenueBs: n(d.room_revenue_bs),
    });
  }
  dias.sort((a, b) => a.fecha.localeCompare(b.fecha));

  const suma = {
    potential: dias.reduce((s, d) => s + d.potential, 0),
    ocupadas: dias.reduce((s, d) => s + d.ocupadas, 0),
    personas: dias.reduce((s, d) => s + d.personas, 0),
    roomRevenueBs: dias.reduce((s, d) => s + d.roomRevenueBs, 0),
  };
  const gt = g.grand_total;
  const grandTotal = gt
    ? {
        potential: n(gt.potential),
        ocupadas: n(gt.ocupadas),
        personas: n(gt.personas),
        roomRevenueBs: n(gt.room_revenue_bs),
      }
    : null;

  if (!g.sede_codigo) issues.push('No se detectó la sede del reporte.');
  if (!g.room_type) issues.push('No se detectó el tipo de habitación del reporte.');
  if (dias.length === 0) issues.push('El reporte no trae ningún día.');
  if (diasConBlanco.length > 0) {
    issues.push(
      `Celda(s) vacía(s) en el PDF, leídas como 0: ${diasConBlanco.join(', ')}. El Grand Total del reporte hace lo mismo.`,
    );
  }
  if (grandTotal) {
    // Ocupadas es la cifra que se consume aguas arriba: un descuadre ahí es una
    // fila perdida, no un mes flojo. El revenue se compara con holgura de 1 Bs
    // por el redondeo de la impresión.
    if (suma.ocupadas !== grandTotal.ocupadas) {
      issues.push(
        `Los días suman ${suma.ocupadas} habitaciones ocupadas y el Grand Total dice ${grandTotal.ocupadas}. Falta o sobra alguna fila.`,
      );
    }
    if (Math.abs(suma.roomRevenueBs - grandTotal.roomRevenueBs) > 1) {
      issues.push(
        `El Room Revenue de los días suma ${suma.roomRevenueBs.toFixed(2)} y el Grand Total dice ${grandTotal.roomRevenueBs.toFixed(2)}.`,
      );
    }
  }

  return {
    sedeCodigo: g.sede_codigo,
    roomType: g.room_type ? g.room_type.trim() : null,
    descripcion: g.descripcion?.trim() || null,
    dias,
    suma,
    grandTotal,
    diasConBlanco,
    issues,
  };
}

// ---------------------------------------------------------------------------
// Agregación para el dashboard
// ---------------------------------------------------------------------------

/** Una fila de `habitacion_diaria` ya resuelta a ids internos. */
export interface RoomTypeEntry {
  sedeId: number;
  fecha: string;
  roomType: string;
  descripcion: string | null;
  potential: number;
  ocupadas: number;
  personas: number;
  roomRevenueBs: number;
}

export interface RoomTypeTotalRange {
  roomType: string;
  descripcion: string | null;
  /** Noches vendidas (Σ habitaciones ocupadas). */
  noches: number;
  /** Habitación-noches disponibles de ese tipo en los días reportados. */
  disponibles: number;
  personas: number;
  roomRevenueBs: number;
  /** Fracción 0..1: ocupación DEL TIPO sobre su propio inventario. */
  occPct: number | null;
  /** ADR del tipo, en Bs (revenue / noches). */
  adrBs: number | null;
  /** Personas por noche vendida: mide si el tipo se vende sencillo o doble. */
  personasPorNoche: number | null;
  /** Fracción 0..1 sobre las noches de los tipos REPORTADOS. */
  share: number | null;
  /** Días distintos con fila para este tipo. */
  dias: number;
}

export interface RoomTypesAggregate {
  /** Noches de los tipos reportados (NO necesariamente las del hotel). */
  noches: number;
  disponibles: number;
  roomRevenueBs: number;
  occPct: number | null;
  adrBs: number | null;
  tipos: RoomTypeTotalRange[];
  /** El tipo más vendido del período, o null. */
  lider: RoomTypeTotalRange | null;
  /** Días distintos con alguna fila cargada. */
  dias: number;
}

/** Filtra por rango de fechas y (si se pasa) por sedes. */
export function filterRoomTypes(
  entries: readonly RoomTypeEntry[],
  opts: { from: string; to: string; sedeIds?: readonly number[] },
): RoomTypeEntry[] {
  const sedes = opts.sedeIds == null ? null : new Set(opts.sedeIds);
  return entries.filter(
    (e) => e.fecha >= opts.from && e.fecha <= opts.to && (sedes == null || sedes.has(e.sedeId)),
  );
}

/**
 * Agrega por tipo de habitación.
 *
 * `occPct` de cada tipo se calcula contra SU propio inventario en los días que
 * el reporte imprimió: es lo único comparable entre tipos de tamaños muy
 * distintos (SGD tiene 19 habitaciones y SWP tiene 3).
 */
export function aggregateRoomTypes(entries: readonly RoomTypeEntry[]): RoomTypesAggregate {
  const porTipo = new Map<string, RoomTypeTotalRange & { _dias: Set<string> }>();
  const dias = new Set<string>();

  for (const e of entries) {
    const t = porTipo.get(e.roomType) ?? {
      roomType: e.roomType,
      descripcion: e.descripcion,
      noches: 0,
      disponibles: 0,
      personas: 0,
      roomRevenueBs: 0,
      occPct: null,
      adrBs: null,
      personasPorNoche: null,
      share: null,
      dias: 0,
      _dias: new Set<string>(),
    };
    t.noches += e.ocupadas;
    t.disponibles += e.potential;
    t.personas += e.personas;
    t.roomRevenueBs += e.roomRevenueBs;
    t.descripcion = t.descripcion ?? e.descripcion;
    t._dias.add(e.fecha);
    porTipo.set(e.roomType, t);
    dias.add(e.fecha);
  }

  const noches = [...porTipo.values()].reduce((s, t) => s + t.noches, 0);
  const tipos = [...porTipo.values()]
    .map(({ _dias, ...t }) => ({
      ...t,
      dias: _dias.size,
      occPct: ratio(t.noches, t.disponibles),
      adrBs: ratio(t.roomRevenueBs, t.noches),
      personasPorNoche: ratio(t.personas, t.noches),
      share: ratio(t.noches, noches),
    }))
    // Desempate alfabético: dos tipos con las mismas noches deben salir siempre
    // en el mismo orden, o el ranking "baila" entre recargas.
    .sort((a, b) => b.noches - a.noches || a.roomType.localeCompare(b.roomType, 'es'));

  const disponibles = tipos.reduce((s, t) => s + t.disponibles, 0);
  const roomRevenueBs = tipos.reduce((s, t) => s + t.roomRevenueBs, 0);

  return {
    noches,
    disponibles,
    roomRevenueBs,
    occPct: ratio(noches, disponibles),
    adrBs: ratio(roomRevenueBs, noches),
    tipos,
    lider: tipos[0] ?? null,
    dias: dias.size,
  };
}

/** Prompt de extracción del reporte "Statistics - Room Type". */
export const GEMINI_ROOMTYPE_PROMPT = `Eres un extractor de datos del reporte hotelero "Statistics - Room Type" (estadísticas diarias de UN tipo de habitación).
Devuelve EXCLUSIVAMENTE un objeto JSON válido (sin markdown ni texto adicional) con este contrato.

Reglas:
1. Identifica la SEDE por el encabezado del reporte. Devuelve uno de: ${SEDE_CODES.join(', ')} en "sede_codigo" (o null si no es claro).
   - "Lidotel Hotel Boutique Margarita" => "MGTA"; "... Barquisimeto" => "BQTO"; "... Valencia" => "VAL"; "... San Cristobal" => "SC"; "Hotel Centro Lido" => "CCS"; "Agua Dorada" => "AD".
   - Si el encabezado dice solo "Lidotel Hotel Boutique" SIN ciudad, es Paraguaná => "PAR".
2. El reporte es de UN SOLO tipo de habitación. Devuelve su código en "room_type" (columna "Room Type") y su texto largo en "descripcion" (columna "Description"). El pie confirma el código en "Filter Room Type : XXX".
   - Los códigos son de la forma <SG|TW|SW><D|P|E>: SGD, TWD, SWD (DELUXE), SGP, TWP, SWP (PREMIUM), SGE, TWE, SWE (EXECUTIVE). Pueden aparecer otros: transcribe SIEMPRE el que imprima el reporte.
   - TOMA EL CÓDIGO DEL REPORTE, NUNCA del nombre del archivo: llegan archivos rotulados «Standard» cuyo contenido es EXECUTIVE.
3. La tabla trae UNA FILA POR DÍA con estas columnas, en este orden: Date, Room Type, Description, "Potential Rms.", "Rms. Occ.", "Prs.", "Room Revenue".
   Devuélvelas en "dias": [{ "fecha": "2026-07-01", "potential": 19, "ocupadas": 14, "personas": 14, "room_revenue_bs": 597861.99 }, …].
   - Las fechas vienen en DD-MM-YY (ej. "01-07-26" = 1 de julio de 2026). Conviértelas a "YYYY-MM-DD".
   - "Room Revenue" es un número en bolívares con separador de miles por coma y decimales por punto ("1,655,734.54" => 1655734.54). Devuélvelo como número, sin comas.
4. NO INVENTES DÍAS. El reporte se salta los días sin movimiento: si no aparece el 30, no lo incluyas. Devuelve solo las filas impresas.
5. Si una celda está VACÍA, devuelve 0 en ese campo (el Grand Total del propio reporte las suma como 0). No la omitas ni desplaces las demás columnas de esa fila.
6. Extrae también la fila "Grand Total" en "grand_total": { "potential": …, "ocupadas": …, "personas": …, "room_revenue_bs": … }. NO la metas dentro de "dias".
7. Alinea fila por fila: localiza la fecha a la izquierda y desplázate por SU MISMA fila hasta cada columna. No mezcles valores de filas vecinas.
8. No calcules ni estimes nada: solo transcribe lo que ves.`;
